import { faBriefcase } from '@fortawesome/pro-solid-svg-icons/faBriefcase';
import { faPencil } from '@fortawesome/pro-solid-svg-icons/faPencil';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	AddressDataUpdateType,
	AddressItemType,
	AddressType,
	deleteAddress,
	GetItemCounts,
	useInfiniteAddresses,
	useUpdateAddressData,
} from '@ukpostbox/client-data';
import {
	Button,
	ColourStateSolid,
	ContentBlock,
	ContentLoader,
	FlexAlign,
	FlexJustify,
	InfiniteLoadTrigger,
	ItemRow,
	ItemRowHeader,
	ItemRowSize,
	StackHorizontal,
	StackVertical,
	ToggleSwitch,
	ToggleType,
	useComboBoxSimple,
	useLayout,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import React, { useEffect, useRef, useState } from 'react';
import { AddAddressModal } from '../../../_Global/Components/Address/AddAddress/AddAddressModal';
import { AddAddressTypeButtons } from '../../../_Global/Components/Address/AddAddress/AddAddressTypeButtons';
import SelectEverythingBanner from '../../../_Global/Components/SelectEverythingBanner';
import SelectionBar from '../../../_Global/Components/SelectionBar';
import { useSite } from '../../../_Global/Providers/SiteInterfaceProvider';

function AddressBook() {
	const { t } = useTranslation();
	const { isXL } = useLayout();
	const { currentLocale, updatePageMeta } = useSite();
	const [createAddressOpen, setCreateAddressOpen] = useState<boolean>(false);
	const [editData, setEditData] = useState<AddressType | null>(null);

	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const { status, data, error, isFetchingNextPage, hasNextPage, fetchNextPage, isStale, isRefetching } =
		useInfiniteAddresses();

	const {
		allSelected,
		visibleSelected,
		visibleIndeterminate,
		selectedItems,
		ToggleAll,
		ToggleVisible,
		ToggleSelected,
		ResetSelection,
	} = useComboBoxSimple();

	const updateAddress = useUpdateAddressData();

	const addressContainerRef = useRef<any>(null);

	const HandleDelete = (addressId?: any) => {
		if (selectedItems.length > 0) {
			deleteAddress(selectedItems[0]).then((r) => {
				updateAddress.mutate({
					newData: [selectedItems[0]],
					updateType: AddressDataUpdateType.DELETE,
				});
				ResetSelection();
			});
		} /* else {
            updateAddress.mutate({
                forwardedFunction: deleteAddress,
                newData: selectedIds,
                tagIds: selectedIds,
                updateType: AddressDataUpdateType.DELETE
            })

            ToggleAll(false);
        } */
	};

	const HandleEdit = (thisData: AddressType) => {
		setEditData(thisData);
		setCreateAddressOpen(true);
	};

	const CreateModalClose = () => {
		setEditData(null);
		setCreateAddressOpen(false);
	};

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data as any));
		}
	}, [data]);

	useEffect(() => {
		updatePageMeta({
			title: t('settings.addressBook.title'),
		});
	}, [currentLocale]);

	return (
		<>
			<StackHorizontal align={FlexAlign.Center} justify={FlexJustify.Between} extraClass='mb-xs'>
				<h2 className='mb-0'>{t('settings.addressBook.title')}</h2>
				<AddAddressTypeButtons name='AddressBookAdd' />
			</StackHorizontal>

			<div
				className={`card address-book-rows ${selectedItems.length > 0 ? 'address-book-rows-selected' : ''}`}
				ref={addressContainerRef}
			>
				<ItemRowHeader sticky title='Address Book List Header'>
					<div className='item-row-checkbox'>
						<ToggleSwitch
							name='addressesAll'
							label='addresses-all'
							invalid={false}
							type={ToggleType.Checkbox}
							defaultChecked={allSelected || (!visibleIndeterminate && visibleSelected)}
							isIndeterminate={!allSelected && visibleIndeterminate}
							onChange={(e: any) => ToggleVisible(data, 'value', !visibleSelected)}
							hideLabel
						/>
					</div>

					{selectedItems.length === 0 && (
						<>
							<div>{t('settings.addressBook.headings.name')}</div>
							<div />
							<div>{t('settings.addressBook.headings.address')}</div>
							<div />
						</>
					)}

					{selectedItems.length > 0 && (
						<SelectionBar selectedIds={selectedItems} clearSelectionCallback={() => ResetSelection()}>
							<Button
								title={t('actions.delete')}
								icon={faTrash}
								iconOnly={!isXL}
								colour={ColourStateSolid.Alert}
								squared
								onClick={() => HandleDelete()}
							/>
						</SelectionBar>
					)}
				</ItemRowHeader>

				{itemCounts.loaded !== itemCounts.total &&
					selectedItems.length !== itemCounts.total &&
					visibleSelected && (
						<SelectEverythingBanner
							selectedCount={allSelected ? itemCounts.total : selectedItems.length}
							selectedLabel={allSelected ? t('actions.selectLoaded') : t('actions.selectAll')}
							callback={() => ToggleAll(!allSelected)}
						/>
					)}

				{status === 'pending' ? (
					<ContentLoader childCount={10} childHeight='7em' />
				) : status === 'error' ? (
					<ContentBlock error title={error.name} content={error.message} />
				) : (
					<>
						{data.pages.map((group: any) =>
							group.data.map((address: AddressItemType, index: number) => (
								<ItemRow
									key={`addressBookEntry${address.value}`}
									title={`addressEntry_${address.label}`}
									static
									size={ItemRowSize.Large}
									selected={allSelected || selectedItems.includes(address.object.addressId)}
								>
									<div
										className='item-row-checkbox'
										onClick={(e) => e.stopPropagation()}
										onKeyDown={(e) => (e.key === 'Enter' ? e.stopPropagation() : undefined)}
										role='button'
										tabIndex={0}
									>
										<ToggleSwitch
											name={`addressItem-${index}`}
											label={`addressItem-${index}`}
											invalid={false}
											type={ToggleType.Checkbox}
											defaultChecked={
												allSelected || selectedItems.includes(address.object.addressId)
											}
											onChange={(e: any) =>
												ToggleSelected(address.object.addressId, e.currentTarget.checked)
											}
											hideLabel
										/>
									</div>
									<div>{address.object.name}</div>
									<div>
										{address.object.businessName && (
											<FontAwesomeIcon icon={faBriefcase} size='1x' />
										)}
									</div>
									<StackVertical>
										<div>{address.object.businessName}</div>
										{address.object.propertyName}, {address.object.addressLine1},{' '}
										{address.object.addressLine2}, {address.object.postCode},{' '}
										{address.object.countryCode}
									</StackVertical>
									<div>
										<Button
											title={t('actions.edit')}
											onClick={() => HandleEdit(address.object)}
											colour={ColourStateSolid.Secondary}
											squared
											iconOnly
											icon={faPencil}
										/>
									</div>
								</ItemRow>
							)),
						)}

						<InfiniteLoadTrigger
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isStale={isStale}
							isRefetching={isRefetching}
							isFetchingNextPage={isFetchingNextPage}
						/>
					</>
				)}
			</div>

			<AddAddressModal
				existingData={editData}
				isOpen={createAddressOpen}
				closeCallback={() => CreateModalClose()}
			/>
		</>
	);
}

export default AddressBook;
